import { Injectable, inject } from '@angular/core';
import {
  FsConnectionService,
  FsCryptService,
  FsDataService,
  FsErrorResponse,
  FsOrganisationService,
  FsRequestService,
  RequestExportService,
} from '@fairandsmart/angular';
import {
  DataCreationOptions,
  DataFormat,
  DataHelper,
  DataUpdateOptions,
  DecipheredData,
  EntityCollection,
  EntityIdentifierHelper,
  EntityName,
  EntityReference,
  FolderEntry,
  FsConnection,
  FsConnectionEndpoint,
  FsData,
  FsHelper,
  FsMessage,
  FsRequest,
  MessageHelper,
  RequestFilter,
  RequestStatus,
  SessionKey,
  SharedPds,
} from '@fairandsmart/types';
import { TranslateService } from '@ngx-translate/core';
import {
  concat, EMPTY, from, Observable, of, throwError, zip,
} from 'rxjs';
import {
  bufferCount, catchError, concatMap, last, map, mergeMap, tap,
} from 'rxjs/operators';
import { AlertService } from '@Services/alert.service';
import { OrgaService } from '@Services/orga.service';
import { FileHelper } from '@Common/file-helper';
import { FlexSearchService } from '../../../index/flex-search.service';
import { RequestIndexerService } from '../../../index/indexer/request-indexer.service';
import { SharedPdsIndexerService } from '../../../index/indexer/shared-pds-indexer.service';
import { MessageWriterAttachment, MessageWriterEvent } from './message-writer/message-writer.component';
import { BackgroundService } from '../../../workers/worker.service';

@Injectable({ providedIn: 'root' })
export class RequestService {
  private readonly orgaService = inject(OrgaService);

  private readonly fsDataService = inject(FsDataService);

  private readonly fsRequestService = inject(FsRequestService);

  private readonly fsOrganisationService = inject(FsOrganisationService);

  private readonly fsConnectionService = inject(FsConnectionService);

  private readonly flexSearchService = inject(FlexSearchService);

  private readonly sharedPdsIndexer = inject(SharedPdsIndexerService);

  private readonly requestIndexer = inject(RequestIndexerService);

  private readonly requestExportService = inject(RequestExportService);

  private readonly translateService = inject(TranslateService);

  private readonly alert = inject(AlertService);

  private readonly cryptService = inject(FsCryptService);

  backgroundService = inject(BackgroundService);

  static readonly NEW_MODEL_KEYWORD = 'new';

  static readonly CLONE_MODEL_KEYWORD = 'clone';

  constructor() {
    this.requestIndexer.startBackgroundIndexation();
    this.sharedPdsIndexer.startBackgroundIndexation();
  }

  get(id: string): Observable<FsRequest> {
    return this.fsRequestService.get(id);
  }

  search(filter?: RequestFilter): Observable<EntityCollection<FsRequest>> {
    return this.fsRequestService.search(filter).pipe(
      tap((response: EntityCollection<FsRequest>) => {
        this.flexSearchService.queueEntities(response.values, true, false);
      }),
    );
  }

  getCount(filter: RequestFilter): Observable<number> {
    const clone: RequestFilter = structuredClone(filter);
    clone.limit = 0;
    return this.fsRequestService.search(clone).pipe(map((response) => response.size));
  }

  createMessage(messageDataId: string, message: FsMessage, messageRef: EntityReference, connection: FsConnection)
    : Observable<DecipheredData<FsMessage>> {
    const helper = new MessageHelper(message);
    return from(helper.hashMe()).pipe(
      mergeMap(() => {
        const options: DataCreationOptions = {
          id: messageDataId,
          reference: messageRef,
          source: this.orgaService.reference,
          format: DataFormat.MESSAGE_V,
          content: message,
          endpoint: this.orgaService.reference,
          ciphererId: this.orgaService.id,
          sessionKeys: this.getSessionKeys(connection),
          wrapResponse: true,
          tags: [EntityIdentifierHelper.toEntityReference(connection)],
          sharedData: !connection.secure,
        };
        return this.backgroundService.createData<FsMessage>(options);
      }),
    );
  }

  updateMessage(currentData: FsData, message: FsMessage): Observable<DecipheredData<FsMessage>> {
    const helper = new MessageHelper(message);
    return from(helper.hashMe()).pipe(
      mergeMap(() => {
        const options: DataUpdateOptions = {
          dataId: currentData.id,
          content: helper.entity,
          format: DataFormat.MESSAGE_V,
          ciphererId: this.orgaService.id,
          sessionKeys: DataHelper.getSessionKeys(currentData, 1),
          endpoint: this.orgaService.reference,
          tags: currentData.tags,
          wrapResponse: true,
          snapshot: true,
        };
        return this.backgroundService.updateData<FsMessage>(options);
      }),
    );
  }

  openRequest(requestId: string): Observable<FsRequest | null> {
    return this.alert.promptConfirmDialog({
      data: {
        title: 'REQUEST_DETAIL.OPEN_REQUEST.TITLE',
        content: 'REQUEST_DETAIL.OPEN_REQUEST.MESSAGE',
        confirm: 'COMMON.BUTTONS.CONFIRM',
      },
    }).afterClosed().pipe(
      mergeMap((confirmed: boolean) => {
        if (confirmed) {
          return this.fsRequestService.updateStatus(requestId, RequestStatus.OPEN);
        }
        return of(null);
      }),
    );
  }

  exportRequest(request: FsRequest): Observable<void> {
    this.requestExportService.setDeciphererId(this.orgaService.id);
    return this.requestExportService.exportRequest(request).pipe(
      map((zipBlob: Blob) => {
        const i18nRequestTypeString = this.translateService.instant(`REQUEST_TYPE.${request.type}`).toLowerCase();
        const fileName = `request-${i18nRequestTypeString}-${this.orgaService.name}-${request.id.substr(0, 6)}-${new Date(request.submissionDate).toISOString().replace(/T.*/, '').replace(':', '')}`;
        return FileHelper.saveBlobAsZip(zipBlob, fileName);
      }),
      catchError((err) => {
        this.alert.error('REQUEST_DETAIL.ERROR_EXPORTING_REQUEST', err);
        return EMPTY;
      }),
    );
  }

  deleteRequestResponseTemplate(modelId: string): Observable<boolean> {
    return this.alert.promptConfirmDialog({
      data: {
        title: 'REQUEST.TEMPLATE.DELETE_PROMPT.TITLE',
        content: 'REQUEST.TEMPLATE.DELETE_PROMPT.MESSAGE',
        confirm: 'COMMON.BUTTONS.DELETE',
      },
    }).afterClosed().pipe(
      mergeMap((confirmed: boolean) => {
        if (confirmed) {
          return this.fsOrganisationService.deleteRequestResponseTemplate(this.orgaService.id, modelId).pipe(
            map(() => {
              this.alert.success('REQUEST.TEMPLATE.DELETE_PROMPT.SUCCESS');
              return true;
            }),
          );
        }
        return of(false);
      }),
      catchError((response: FsErrorResponse) => {
        this.alert.apiError(response.error, 'REQUEST.TEMPLATE.DELETE_PROMPT.ERROR', { timeOut: 10000 });
        return of(false);
      }),
    );
  }

  saveSharedPds(newValue: SharedPds, request: FsRequest, currentSharedPdsId: string | null): Observable<FsData> {
    return this.saveSharedPdsInternal(newValue, EntityIdentifierHelper.toEntityReference(request), request.reference, currentSharedPdsId)
      .pipe(
        tap((data: FsData) => {
          this.flexSearchService.queueEntities([data], true, false);
        }),
      );
  }

  updateRequestEmitter(requestId: string, emitter: EntityReference): Observable<FsRequest> {
    return this.fsRequestService.updateRequestEmitter(requestId, emitter).pipe(
      tap((request: FsRequest) => {
        this.flexSearchService.queueEntities([request], true, false);
      }),
    );
  }

  private saveSharedPdsInternal(
    newValue: SharedPds,
    sharedPdsRef: EntityReference,
    connectionRef: EntityReference,
    currentSharedPdsId: string | null,
  ): Observable<FsData> {
    return this.fsConnectionService.get(EntityIdentifierHelper.extractId(connectionRef)).pipe(
      mergeMap((connection: FsConnection) => {
        if (currentSharedPdsId == null) {
          return this.backgroundService.createData({
            reference: sharedPdsRef,
            source: this.orgaService.reference,
            content: newValue,
            format: DataFormat.SHARED_PDS_V,
            ciphererId: this.orgaService.id,
            sessionKeys: this.getSessionKeys(connection),
            endpoint: this.orgaService.reference,
            wrapResponse: false,
            tags: [connectionRef, sharedPdsRef],
          });
        }
        return this.backgroundService.updateData({
          dataId: currentSharedPdsId,
          content: newValue,
          format: DataFormat.SHARED_PDS_V,
          ciphererId: this.orgaService.id,
          sessionKeys: this.getSessionKeys(connection),
          endpoint: this.orgaService.reference,
          wrapResponse: false,
          tags: [connectionRef, sharedPdsRef],
        });
      }),
    );
  }

  getSessionKeys(connection: FsConnection): { [id: string]: SessionKey } {
    const sessionKeys: { [key: string]: SessionKey } = {};
    connection.endpoints.forEach((endpoint: FsConnectionEndpoint) => {
      if (endpoint.key && endpoint.entityIdentifier.entityName !== EntityName.RECIPIENT) {
        sessionKeys[endpoint.entityIdentifier.entityId] = endpoint.key;
      }
    });
    return sessionKeys;
  }

  getConnectionSessionKeys(request: FsRequest): Observable<{ [id: string]: SessionKey }> {
    return this.fsConnectionService.get(EntityIdentifierHelper.extractId(request.reference)).pipe(
      map((connection: FsConnection) => this.getSessionKeys(connection)),
    );
  }

  saveDraft($event: MessageWriterEvent, request: FsRequest, currentMessageData: FsData | null): Observable<DecipheredData<FsMessage>> {
    return this.internalSaveDraft($event, EntityIdentifierHelper.toEntityReference(request), request.reference, currentMessageData);
  }

  private internalSaveDraft(
    $event: MessageWriterEvent,
    messageRef: EntityReference,
    connectionRef: EntityReference,
    currentMessageData: FsData | null,
  ): Observable<DecipheredData<FsMessage>> {
    const messageDataId = currentMessageData != null ? currentMessageData.id : FsHelper.generateUuid();
    const { message } = $event;
    message.content.emitter = {
      fsId: this.orgaService.id,
    };
    message.content.date = new Date().toISOString();
    message.content.language = 'fr';
    return this.cleanUpAttachments($event).pipe(
      mergeMap(() => this.fsConnectionService.get(EntityIdentifierHelper.extractId(connectionRef))),
      mergeMap((connection: FsConnection) => this.createMessageAttachments(messageDataId, $event.files, connection).pipe(
        mergeMap((attachments: FolderEntry[]) => {
          message.content.attachments = attachments;
          if (currentMessageData != null) {
            return this.updateMessage(currentMessageData, message);
          }
          return this.createMessage(messageDataId, message, messageRef, connection);
        }),
      )),
    );
  }

  deleteDraft(messageData: FsData, message: FsMessage): Observable<void> {
    let obs: Observable<void>;
    if (message.content.attachments.length > 0) {
      obs = from(message.content.attachments).pipe(
        concatMap((attachment) => this.fsDataService.delete(EntityIdentifierHelper.extractId(attachment.reference))),
        last(),
      );
    } else {
      obs = of(null);
    }
    return obs.pipe(
      mergeMap(() => this.fsDataService.delete(messageData.id)),
    );
  }

  createMessageAttachments(messageDataId: string, files: MessageWriterAttachment[], connection: FsConnection): Observable<FolderEntry[]> {
    if (files.length === 0) {
      return of([]);
    }
    return from(files).pipe(
      concatMap((file: MessageWriterAttachment) => {
        if (file.entry) {
          file.status = 'done';
          return of(file.entry);
        }
        file.status = 'uploading';
        return FsHelper.readFileAsArrayBuffer(file.file).pipe(
          mergeMap((content) => {
            const shareKey$ = connection.secure ? of(undefined)
              : this.cryptService.exportSessionKey(this.getSessionKeys(connection)[this.orgaService.id]);
            return zip(
              this.createAttachment(content, messageDataId, connection),
              shareKey$,
            );
          }),
          map(([data, shareKey]: [FsData, string]) => {
            file.status = 'done';
            file.entry = {
              name: file.file.name,
              mimeType: file.file.type,
              size: file.file.size,
              reference: EntityIdentifierHelper.toEntityReference(data),
              shareKey,
              sharingId: data.sharingId,
            } as FolderEntry;
            return file.entry;
          }),
          catchError((err) => {
            file.status = 'error';
            return throwError(() => new Error(err));
          }),
        );
      }),
      bufferCount(files.length),
    );
  }

  createAttachment(content: ArrayBuffer, messageDataId: string, connection: FsConnection): Observable<FsData> {
    const options: DataCreationOptions = {
      reference: EntityIdentifierHelper.toEntityReference(EntityName.DATA, messageDataId),
      source: this.orgaService.reference,
      content,
      format: DataFormat.FILE_V,
      ciphererId: this.orgaService.id,
      sessionKeys: this.getSessionKeys(connection),
      endpoint: this.orgaService.reference,
      wrapResponse: false,
      sharedData: !connection.secure,
      tags: [EntityIdentifierHelper.toEntityReference(connection)],
    };
    return this.backgroundService.createData(options);
  }

  private cleanUpAttachments($event: MessageWriterEvent): Observable<void> {
    if ($event.message?.content.attachments == null || $event.message.content.attachments.length === 0) {
      return of(null);
    }
    const toBeRemoved: Observable<void>[] = $event.message.content.attachments
      .filter((entry: FolderEntry) => !$event.files.some((f) => f.entry === entry))
      .map((entry) => this.fsDataService.delete(EntityIdentifierHelper.extractId(entry.reference)));
    if (toBeRemoved.length > 0) {
      return concat(toBeRemoved).pipe(
        last(),
        map(() => null),
      );
    }
    return of(null);
  }

  isExpired(request: FsRequest, now: number = new Date().getTime()): boolean {
    return request.status !== RequestStatus.CLOSED && request.expirationDate && request.expirationDate < now;
  }
}
